export type AccessNode = {
  id: string;
  label: string;
  children?: AccessNode[];
};

type MenuAccessItem = {
  label: string;
  children?: MenuAccessItem[];
};

const toAccessId = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const buildAccessTree = (
  items: MenuAccessItem[],
  parentId?: string
): AccessNode[] =>
  items.map(item => {
    const localId = toAccessId(item.label);
    const id = parentId ? `${parentId}__${localId}` : localId;
    return {
      id,
      label: item.label,
      children: item.children ? buildAccessTree(item.children, id) : undefined,
    };
  });

/** Built from Admin sidebar menu groups (FineaseAdminLayout). */
const ADMIN_MENU_ACCESS_CONFIG: MenuAccessItem[] = [
  { label: "Dashboard" },
  { label: "User Management" },
  {
    label: "NPS",
    children: [
      { label: "NPS Users" },
      { label: "Payment Entry" },
      { label: "Settlements" },
    ],
  },
  {
    label: "IPO",
    children: [
      { label: "IPO Management" },
      { label: "Member Management" },
      { label: "Applications" },
      { label: "Analytics" },
      { label: "Past Orders" },
    ],
  },
  {
    label: "Screener",
    children: [
      { label: "Company Master" },
      { label: "Category Master" },
      { label: "Query Field Master" },
      { label: "Query Master Admin" },
      { label: "Query Master User" },
    ],
  },
  { label: "Bonds" },
  { label: "Fixed Deposits" },
  {
    label: "Physical Gold",
    children: [
      { label: "Gold Dashboard" },
      { label: "Product Master" },
      { label: "Delivery Master" },
      { label: "Gold Orders" },
      { label: "Gold Settlements" },
      { label: "Commission" },
    ],
  },
  { label: "Contact Us" },
  {
    label: "Logs",
    children: [
      { label: "User Audit Logs" },
      { label: "API Issue Logs" },
      { label: "Login Activity" },
      { label: "SMS Logs" },
    ],
  },
  {
    label: "Role Management",
    children: [{ label: "Users" }, { label: "Roles" }],
  },
  {
    label: "CMS",
    children: [{ label: "Banners" }, { label: "Holidays" }],
  },
  {
    label: "Email",
    children: [{ label: "Website" }],
  },
];

export const DEFAULT_ACCESS_TREE: AccessNode[] = buildAccessTree(
  ADMIN_MENU_ACCESS_CONFIG
);

export type PermissionRowState = {
  on: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
};

export function flattenAccessIds(nodes: AccessNode[]): string[] {
  const out: string[] = [];
  const walk = (list: AccessNode[]) => {
    for (const n of list) {
      out.push(n.id);
      if (n.children) {
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return out;
}

export function buildPermissionState(
  nodes: AccessNode[],
  preset?: Record<string, Partial<PermissionRowState>>
): Record<string, PermissionRowState> {
  const ids = flattenAccessIds(nodes);
  const state: Record<string, PermissionRowState> = {};
  for (const id of ids) {
    const p = preset?.[id];
    const pAny = p as any;
    state[id] = {
      on: p?.on ?? false,
      // Backward compatible with old keys (v/i).
      view: p?.view ?? pAny?.v ?? false,
      edit: p?.edit ?? pAny?.i ?? false,
      delete: p?.delete ?? false,
    };
  }
  return state;
}
